import React, { createContext, useContext, useState, useEffect } from "react";

interface SiteSettings {
  // Hero Section
  heroTitle: string;
  heroSubtitle: string;
  heroButtonText: string;
  heroButtonColor: string;
  heroButtonTextColor: string;
  heroBackgroundColor: string;

  // CTA Section
  ctaTitle: string;
  ctaSubtitle: string;
  ctaButtonText: string;
  ctaButtonColor: string;
  ctaButtonTextColor: string;
  ctaBackgroundColor: string;

  // Global Colors
  primaryColor: string;
  secondaryColor: string;

  // Header
  logoColor: string;
  headerButtonText: string;
  headerButtonColor: string;
  headerButtonTextColor: string;

  // Features Section
  featuresTitle: string;
  featuresSubtitle: string;
  featuresButtonText: string;
  featuresButtonColor: string;
  featuresButtonTextColor: string;

  // How It Works Section
  howItWorksTitle: string;
  howItWorksSubtitle: string;
  howItWorksVideoTitle: string;
  howItWorksVideoSubtitle: string;

  // Partners Section
  partnersTitle: string;
  partnersSubtitle: string;
  partnershipEmail: string;

  // Institutions Section
  institutionsTitle: string;
  institutionsSubtitle: string;
  institutionsButtonText: string;
  institutionsRebalancingDescription: string;
  institutionsSecurityDescription: string;
  institutionsAssetDescription: string;

  // Features Cards
  featuresCards: Array<{
    name: string;
    description: string;
  }>;

  // Institutions Features
  institutionsFeatures: Array<{
    name: string;
    description: string;
  }>;

  // How It Works Steps
  howItWorksSteps: Array<{
    step: string;
    title: string;
    description: string;
  }>;
}

const defaultSettings: SiteSettings = {
  // Hero Section
  heroTitle: "Automate Your DeFi Future",
  heroSubtitle:
    "Build powerful DeFi automation workflows without coding. Connect your wallet, set your triggers, and let de5.ai handle the rest.",
  heroButtonText: "Start Building Now",
  heroButtonColor: "#e95c49",
  heroButtonTextColor: "#ffffff",
  heroBackgroundColor: "rgba(249, 250, 251, 0.8)",

  // CTA Section
  ctaTitle: "Architect your financial automations now",
  ctaSubtitle:
    "Be one of the first investors to get access to our platform. Sign up to be notified when we launch!",
  ctaButtonText: "Join Waitlist",
  ctaButtonColor: "#e95c49",
  ctaButtonTextColor: "#ffffff",
  ctaBackgroundColor: "rgba(233, 92, 73, 0.05)",

  // Global Colors
  primaryColor: "#e95c49",
  secondaryColor: "#000000",

  // Header
  logoColor: "#e95c49",
  headerButtonText: "Get Started",
  headerButtonColor: "#e95c49",
  headerButtonTextColor: "#ffffff",

  // Features Section
  featuresTitle: "Powerful Features for DeFi Automation",
  featuresSubtitle:
    "Everything you need to build, deploy, and manage DeFi automation workflows",
  featuresButtonText: "Explore All Features",
  featuresButtonColor: "#e95c49",
  featuresButtonTextColor: "#ffffff",

  // How It Works Section
  howItWorksTitle: "How It Works",
  howItWorksSubtitle: "Build DeFi automation workflows in 4 simple steps",
  howItWorksVideoTitle: "See It In Action",
  howItWorksVideoSubtitle:
    "Watch how easy it is to build and deploy DeFi automation workflows",

  // Partners Section
  partnersTitle: "Partner with de5.ai",
  partnersSubtitle:
    "Join our ecosystem of forward-thinking partners and unlock new revenue streams while delivering cutting-edge DeFi automation solutions to your customers.",
  partnershipEmail: "partnerships@de5.ai",

  // Institutions Section
  institutionsTitle: "For Institutions",
  institutionsSubtitle:
    "Leverage the power of DeFi automation for your institutional clients. Our platform is designed to meet the highest security and compliance standards.",
  institutionsButtonText: "Learn More",
  institutionsRebalancingDescription:
    "Our rebalancing engine ensures your portfolio stays aligned with your strategic goals, even in volatile markets.",
  institutionsSecurityDescription:
    "Built on top of the most secure DeFi protocols, your assets are protected by cutting-edge security measures.",
  institutionsAssetDescription:
    "Access a wide range of DeFi assets, including stablecoins, tokens, and NFTs, all managed with precision and transparency.",

  // Features Cards
  featuresCards: [
    {
      name: "Drag & Drop Builder",
      description:
        "Create complex DeFi workflows with our intuitive visual interface. No coding required.",
    },
    {
      name: "EIP-7702 Smart Wallets",
      description:
        "Secure automation with next-generation smart wallet technology for enhanced security.",
    },
    {
      name: "Real-time Price Feeds",
      description:
        "Powered by Pyth Network oracles for precise, low-latency price data and triggers.",
    },
    {
      name: "Multi-chain Support",
      description:
        "Execute strategies across Ethereum, Polygon, Arbitrum, and other major networks.",
    },
    {
      name: "Advanced Analytics",
      description:
        "Track performance with detailed analytics and insights for all your strategies.",
    },
    {
      name: "DeFAI agents",
      description:
        "Deploy autonomous AI agents that execute complex DeFi strategies across multiple protocols and chains.",
    },
    {
      name: "Quant Strategies",
      description:
        "Implement sophisticated quantitative strategies with customizable parameters and advanced execution logic.",
    },
    {
      name: "Smart Notifications",
      description:
        "Get alerts via Email, Slack, Telegram, or Discord when your strategies execute.",
    },
  ],

  // Institutions Features
  institutionsFeatures: [
    {
      name: "Institutional-grade Custody",
      description:
        "Bank-grade custody solutions with cold storage options and insurance coverage for digital assets.",
    },
    {
      name: "Regulatory Compliance Framework",
      description:
        "Built-in compliance tools for AML/KYC requirements and regulatory reporting across jurisdictions.",
    },
    {
      name: "High-frequency Execution Engine",
      description:
        "Optimized execution engine capable of handling high-volume trades with minimal slippage and latency.",
    },
    {
      name: "Custom Risk Management",
      description:
        "Advanced risk modeling with customizable parameters for VaR, stress testing, and position limits.",
    },
    {
      name: "API-first Integration",
      description:
        "Comprehensive REST and GraphQL APIs for seamless integration with existing institutional systems.",
    },
    {
      name: "Multi-entity Account Structure",
      description:
        "Support for complex organizational structures with sub-accounts, segregated funds, and hierarchical permissions.",
    },
    {
      name: "On-chain Analytics",
      description:
        "Real-time blockchain analytics for transaction monitoring, compliance reporting, and market intelligence.",
    },
    {
      name: "Disaster Recovery Systems",
      description:
        "Enterprise-grade disaster recovery with geo-redundancy and automated failover mechanisms.",
    },
  ],

  // How It Works Steps
  howItWorksSteps: [
    {
      step: "01",
      title: "Sign Up",
      description:
        "Connect your wallet or sign up with email, Google, or Twitter. Get started in seconds with our seamless onboarding.",
    },
    {
      step: "02",
      title: "Build Your Workflow",
      description:
        "Drag and drop components to create your perfect DeFi strategy. Set up DCA, limit orders, swaps, and more.",
    },
    {
      step: "03",
      title: "Automate Execution",
      description:
        "Your workflows execute automatically based on real-time blockchain events and your custom triggers.",
    },
    {
      step: "04",
      title: "Get Notified",
      description:
        "Receive instant notifications via your preferred channels when strategies execute or conditions are met.",
    },
  ],
};

interface SettingsContextType {
  settings: SiteSettings;
  updateSettings: (newSettings: Partial<SiteSettings>) => Promise<void>;
  resetSettings: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load settings from API
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/api/settings", {
        credentials: "include",
      });

      if (response.ok) {
        const savedSettings = await response.json();
        setSettings({ ...defaultSettings, ...savedSettings });
      } else {
        console.error("Failed to load settings from server");
        setError("Failed to load settings");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings: Partial<SiteSettings>) => {
    try {
      const updated = { ...settings, ...newSettings };
      setSettings(updated);

      const response = await fetch("/api/admin/settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(updated),
      });

      if (!response.ok) {
        console.error("Failed to save settings to server");
        setError("Failed to save settings");
        // Revert the local state
        setSettings(settings);
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      setError("Failed to save settings");
      // Revert the local state
      setSettings(settings);
    }
  };

  const resetSettings = async () => {
    try {
      const response = await fetch("/api/admin/settings/reset", {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        // Reload settings from the database after reset
        await loadSettings();
        setError(null);
      } else {
        console.error("Failed to reset settings on server");
        setError("Failed to reset settings");
      }
    } catch (error) {
      console.error("Error resetting settings:", error);
      setError("Failed to reset settings");
    }
  };

  return (
    <SettingsContext.Provider
      value={{ settings, updateSettings, resetSettings, loading, error }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
